import React from 'react'

import { loadMark } from '@/perf/loadTiming'
import { getItemColor } from '@/components/palette/state'
import { cachedSwatchImage, swatchImageKey, whenSwatchImagesLoaded } from '@/swatches/swatchImageCache'
import { swatchKey, type SwatchItem } from '@/swatches/swatchSheet'
import type { VoxelData } from '@/voxelTypes'

/**
 * Palette swatches as images inside ordinary DOM buttons (the kit-style editor bar).
 *
 * One hidden canvas hosts a `SwatchAtlas` (the document runtimes draw every swatch into an atlas
 * texture). Each `SwatchImage` is a small 2D canvas that registers its palette index; whenever the
 * atlas refreshes, the host draws each registered item's atlas cell into its own (hidden) canvas
 * with a one-quad pass and copies that into the item's 2D canvas (`drawImage` of a WebGPU canvas in
 * the same task: a GPU-side copy). Buttons can then lift, ring and scroll like any kit button.
 *
 * Opening a document, the atlas needs a few hundred ms (its own WebGPU device, shader builds for
 * every family on the sheet). Until a picture arrives, each `SwatchImage` shows a soft dot in its
 * item's colour, the same footprint as the swatch, and the picture fades in over it. Pictures drawn
 * before are kept (`swatchImageCache`) and shown at once, without a fade, so a palette seen before
 * appears with the rest of the chrome. When kept pictures cover every swatch on screen, the atlas
 * (its device, its shader builds: a 120-150 ms main-thread task) does not start until the browser
 * has been idle for a while after the open, or until a swatch on screen has no kept picture (an
 * edit, add mode, an item card, recent colours).
 */

/** Fade of a picture over its placeholder dot. */
const FADE_MS = 180
/** With every visible swatch kept, the atlas starts in the first idle period after this long. */
const DEFERRED_START_MS = 2000

type Registry = {
  paintKept: (index: number, canvas: HTMLCanvasElement) => void
  register: (index: number, canvas: HTMLCanvasElement | null) => void
  /** CSS colour of item `index` (its placeholder). */
  colorOf: (index: number) => string
}

const SwatchImagesContext = React.createContext<Registry | null>(null)

/** The atlas's renderer (a WebGPU canvas and the voxel renderers): its own chunk, loaded when the atlas starts. */
const SwatchAtlasHost = React.lazy(() => import('@/swatches/SwatchAtlasHost'))

/** Hosts the atlas for `items` and provides `SwatchImage` with its pictures. */
export function SwatchImagesProvider({ items, cellPx, eager, children }: {
  items: readonly SwatchItem[]
  cellPx: number
  /** Items drawn first (the ones on screen); the rest follow in later passes (`SwatchAtlas.eager`). */
  eager?: number
  children: React.ReactNode
}) {
  const targetsRef = React.useRef(new Map<number, HTMLCanvasElement>())
  const [version, setVersion] = React.useState(0)
  const latestRef = React.useRef({ items, cellPx })
  latestRef.current = { items, cellPx }
  const [keptLoaded, setKeptLoaded] = React.useState(false)
  const [started, setStarted] = React.useState(false)
  const registry = React.useMemo<Registry>(() => {
    /** Shows the kept picture of item `index` unless the canvas shows that swatch already. */
    const paintKept = (index: number, canvas: HTMLCanvasElement) => {
      const { items, cellPx } = latestRef.current
      const item = items[index]
      if (!item) return
      const key = swatchKey(item)
      if (canvas.dataset.key === key) return
      const bitmap = cachedSwatchImage(swatchImageKey(key, canvas.width, cellPx))
      const ctx = bitmap && canvas.getContext('2d')
      if (!bitmap || !ctx) return
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height)
      canvas.dataset.key = key
      showPicture(canvas, false)
    }
    // Pictures stored by an earlier visit arrive shortly after startup.
    whenSwatchImagesLoaded(() => {
      for (const [index, canvas] of targetsRef.current) paintKept(index, canvas)
      setKeptLoaded(true)
    })
    return {
      paintKept,
      register(index, canvas) {
        const targets = targetsRef.current
        if (canvas) {
          if (targets.get(index) === canvas) return
          targets.set(index, canvas)
          paintKept(index, canvas)
        } else if (!targets.delete(index)) return
        setVersion((v) => v + 1)
      },
      colorOf(index) {
        const item = latestRef.current.items[index]
        return item ? `#${(getItemColor(item as VoxelData) >>> 0).toString(16).padStart(6, '0').slice(-6)}` : '#c8c8c8'
      },
    }
  }, [])

  // Kept pictures for the current items (edits included); start the atlas once one is missing.
  React.useEffect(() => {
    if (started || !keptLoaded) return
    for (const [index, canvas] of targetsRef.current) {
      registry.paintKept(index, canvas)
      const item = items[index]
      if (item && canvas.dataset.key !== swatchKey(item)) {
        loadMark('swatch-atlas-start:needed')
        setStarted(true)
        return
      }
    }
  }, [items, keptLoaded, registry, started, version])
  // Everything on screen is kept: start in an idle period a little after the open.
  React.useEffect(() => {
    if (started) return
    let idle = 0
    const timer = window.setTimeout(() => {
      const start = () => { loadMark('swatch-atlas-start:idle'); setStarted(true) }
      if (typeof requestIdleCallback === 'function') idle = requestIdleCallback(start, { timeout: 2000 })
      else start()
    }, DEFERRED_START_MS)
    return () => {
      window.clearTimeout(timer)
      if (idle && typeof cancelIdleCallback === 'function') cancelIdleCallback(idle)
    }
  }, [started])

  return (
    <SwatchImagesContext.Provider value={registry}>
      {children}
      {started && (
        <React.Suspense fallback={null}>
          <SwatchAtlasHost items={items} cellPx={cellPx} eager={eager} targets={targetsRef.current} version={version} />
        </React.Suspense>
      )}
    </SwatchImagesContext.Provider>
  )
}

/**
 * Shows a canvas's picture over its placeholder dot (the canvas's previous sibling): with a short
 * fade the first time the atlas draws it, at once for a kept picture.
 */
export function showPicture(canvas: HTMLCanvasElement, fade: boolean): void {
  if (canvas.dataset.drawn) return
  canvas.dataset.drawn = '1'
  loadMark(fade ? 'swatch-shown:drawn' : 'swatch-shown:kept')
  const dot = canvas.previousElementSibling as HTMLElement | null
  const transition = fade ? `opacity ${FADE_MS}ms ease-out` : 'none'
  canvas.style.transition = transition
  canvas.style.opacity = '1'
  if (dot) {
    dot.style.transition = transition
    dot.style.opacity = '0'
  }
}

/**
 * A palette item's swatch picture: a 2D canvas filled by the nearest `SwatchImagesProvider`, over a
 * soft dot in the item's colour that holds its place until the picture arrives.
 */
export function SwatchImage({ index, size }: { index: number; size: number }) {
  const registry = React.useContext(SwatchImagesContext)
  const dpr = typeof window === 'undefined' ? 1 : Math.min(2, window.devicePixelRatio || 1)
  const px = Math.round(size * dpr)
  const ref = React.useCallback((canvas: HTMLCanvasElement | null) => {
    registry?.register(index, canvas)
  }, [registry, index])
  const color = registry?.colorOf(index) ?? '#c8c8c8'
  // About the footprint of a swatch's body (it fills `SWATCH_FILL` of a picture drawn at 1.5x the item).
  const dot = Math.round(size * 0.56)
  return (
    <span style={{ position: 'relative', display: 'block', width: size, height: size, pointerEvents: 'none' }}>
      <span
        aria-hidden="true"
        style={{
          position: 'absolute',
          left: (size - dot) / 2,
          top: (size - dot) / 2,
          width: dot,
          height: dot,
          borderRadius: '50%',
          background: `radial-gradient(circle at 35% 30%, rgba(255, 255, 255, 0.45), rgba(255, 255, 255, 0) 60%), ${color}`,
          opacity: 0.5,
        }}
      />
      <canvas ref={ref} width={px} height={px} style={{ position: 'absolute', left: 0, top: 0, width: size, height: size, display: 'block', opacity: 0 }} />
    </span>
  )
}
